import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';
import { User, UserGroupNames } from '../models';
import { selectLoadedCurrentUser } from '../store';

const ALLOWED_GROUPS: UserGroupNames[] = [
  UserGroupNames.SAAS_PROVIDER,
  UserGroupNames.SCHOOL_STAFF_ADMINS,
];

const INTERNAL_ADMINS: UserGroupNames[] = [
  UserGroupNames.SERVICE_AND_SUPPORT,
  UserGroupNames.ACCOUNT_MANAGERS,
];

function userHasLicense(user: User): boolean {
  return (
    user.is_superuser ||
    ((user.groups.includes(UserGroupNames.LIMITED_SAAS_ACCESS) ||
      user.groups.includes(UserGroupNames.SAAS_PROVIDER) ||
      INTERNAL_ADMINS.some((group) => user.groups.includes(group)) ||
      user.groups.includes(UserGroupNames.SAAS)) &&
      ALLOWED_GROUPS.some((group) => user.groups.includes(group)))
  );
}

/// Checks that the user has a license or redirects to the purchase page
@Injectable({ providedIn: 'root' })
export class LicensedOrPurchaseGuard implements CanActivate {
  constructor(private router: Router, private store: Store) {}

  async canActivate(): Promise<boolean | UrlTree> {
    // Wait until user.groups is defined and is an array
    const user = await firstValueFrom(
      this.store.pipe(
        selectLoadedCurrentUser,
        filter((u: any) => Array.isArray(u?.groups)),
        take(1)
      )
    );
    return (
      userHasLicense(user) ||
      INTERNAL_ADMINS.some((group) => user.groups.includes(group)) ||
      this.router.parseUrl('/purchase')
    );
  }
}

/// Checks that the user does not already have a license or redirects to
/// the home page
@Injectable({ providedIn: 'root' })
export class NoLicenseGuard {
  constructor(private router: Router, private store: Store) {}

  async canActivate(): Promise<boolean | UrlTree> {
    // Wait until user.groups is defined and is an array
    const user = await firstValueFrom(
      this.store.pipe(
        selectLoadedCurrentUser,
        filter((u: any) => Array.isArray(u?.groups)),
        take(1)
      )
    );
    return (
      !userHasLicense(user) ||
      INTERNAL_ADMINS.some((group) => user.groups.includes(group)) ||
      this.router.parseUrl('/')
    );
  }
}
